/**
 * OverviewCockpit — System Cockpit page for Analytics Overview.
 *
 * Replaces the old pinned-chart grid. Structured as:
 *   A) Executive Strip (7 signal tiles)
 *   B) Health Rings (4 scores)
 *   C) Milestone Governance Timeline
 *   D) Drivers of Risk (activity + WBS ranked lists)
 *   E) Exceptions / Warning Lights
 *   F) What Changed (multi-snapshot only)
 *
 * Data comes from a single GET /api/v1/analytics/cockpit/{projectId}.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useAnalyticsContext } from '@/hooks/useAnalyticsContext';
import { getCockpitSummary } from '@/api/cockpitApi';
import LoadingState from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/analytics/EmptyState';
import { Button } from '@/components/ui/button';

import {
  CockpitTile,
  HealthRing,
  MilestoneStrip,
  ActivityDriverTable,
  WBSDriverTable,
  ExceptionPanel,
  ChangeSummary,
} from '@/components/cockpit';
import type { TileStatus } from '@/components/cockpit';

import type { CockpitPayload, RagStatus } from '@/types/cockpit';
import { toActivitiesUrl } from '@/utils/drillthrough';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ragToTile(rag: RagStatus): TileStatus {
  if (rag === 'ok') return 'ok';
  if (rag === 'warn') return 'warn';
  return 'critical';
}

function formatDateShort(d: string | null): string {
  if (!d) return '—';
  try {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

function deriveTileStatus(value: number, warn: number, crit: number): TileStatus {
  if (value === 0) return 'ok';
  if (value < crit) return 'warn';
  return 'critical';
}

function pctTileStatus(pct: number, warnPct: number, critPct: number): TileStatus {
  if (pct <= 0) return 'ok';
  if (pct < critPct) return 'warn';
  return 'critical';
}

// Section header with optional info tooltip
const SectionHeader: React.FC<{
  title: string;
  subtitle?: string;
  className?: string;
}> = ({ title, subtitle, className }) => (
  <div className={className}>
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {subtitle && (
      <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function OverviewCockpitPage() {
  const { selectedProject, selectedBaselineId } = useGlobalProject();
  const { filters, resetAll } = useAnalyticsContext();
  const navigate = useNavigate();

  const [data, setData] = useState<CockpitPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------- Fetch ----------
  // Call cockpit with project ID only (no snapshot_id), same as Schedule / Risk / Resources / Cost
  // so the backend uses project_import_metadata "latest" and overview shows the same data as other analytics pages.
  useEffect(() => {
    if (!selectedProject?.id) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCockpitSummary(selectedProject.id, {
      baselineId: selectedBaselineId ?? undefined,
      wbs: filters.wbs,
      activityCode: filters.activityCode,
      status: filters.status,
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err?.message ?? 'Failed to load cockpit data'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, selectedBaselineId, filters.wbs, filters.activityCode, filters.status]);

  // ---------- Guards ----------
  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading cockpit data…" size="lg" fullScreen={false} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border bg-card p-8 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        <EmptyState />
      </div>
    );
  }

  const { executive: ex, scores, milestones, drivers, exceptions, change, context } = data;
  const noDataReason = context?.noDataReason ?? null;

  if (noDataReason) {
    return (
      <div className="flex flex-col gap-6">
        <div className="rounded-lg border bg-card p-8">
          <p className="text-sm font-medium text-muted-foreground text-center">{noDataReason}</p>
          <p className="text-xs text-muted-foreground text-center mt-2">Tiles and drivers show N/A when there is no activity data for the selected context or filters.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Button variant="outline" size="sm" onClick={() => resetAll()}>
              Reset filters
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/app/explorer')}>
              Go to Explorer
            </Button>
            <Button variant="default" size="sm" onClick={() => navigate(selectedProject?.id ? `/app/projects/${selectedProject.id}` : '/app/projects')}>
              Import snapshot
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ================================================================
          SECTION A + B: Executive Strip + Health Rings
          ================================================================ */}
      <section className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6">
        {/* A) Executive Strip — 7 tiles */}
        <div>
          <SectionHeader title="Executive Summary" className="mb-3" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {/* 1) Forecast Finish */}
            <CockpitTile
              label="Forecast Finish"
              value={formatDateShort(ex.forecastFinish.date)}
              secondary={
                ex.forecastFinish.varianceDays !== null
                  ? `${ex.forecastFinish.varianceDays > 0 ? '+' : ''}${ex.forecastFinish.varianceDays}d vs baseline`
                  : undefined
              }
              status={
                ex.forecastFinish.date === null
                  ? 'na'
                  : ex.forecastFinish.varianceDays !== null && ex.forecastFinish.varianceDays > 14
                    ? 'critical'
                    : ex.forecastFinish.varianceDays !== null && ex.forecastFinish.varianceDays > 0
                      ? 'warn'
                      : 'ok'
              }
              ctaLabel={ex.forecastFinish.varianceDays === null ? 'Set baseline' : undefined}
              ctaHref="/app/baselines"
            />

            {/* 2) Finish Risk */}
            <CockpitTile
              label="Finish Risk"
              value={ex.finishRisk.rag === 'ok' ? 'OK' : ex.finishRisk.rag === 'warn' ? 'Warn' : 'Critical'}
              status={ragToTile(ex.finishRisk.rag)}
              chips={ex.finishRisk.drivers}
              onClick={() => navigate('/app/analytics/critical-path')}
            />

            {/* 3) Critical Path Pressure */}
            <CockpitTile
              label="Critical Path Pressure"
              value={ex.critical.criticalCount}
              secondary={`Near-critical: ${ex.critical.nearCriticalCount}${
                ex.critical.cpLengthDays !== null ? ` · CP: ${ex.critical.cpLengthDays}d` : ''
              }`}
              status={deriveTileStatus(ex.critical.criticalCount, 1, 20)}
              onClick={() => navigate('/app/analytics/critical-path')}
            />

            {/* 4) Float Health */}
            <CockpitTile
              label="Float Health"
              value={ex.float.negativeCount}
              secondary={`${ex.float.negativePct}% of activities with negative float${
                ex.float.medianDays !== null ? ` · Median: ${ex.float.medianDays}d` : ''
              }`}
              status={pctTileStatus(ex.float.negativePct, 5, 15)}
              onClick={() => navigate('/app/activities?negativeFloat=true')}
            />

            {/* 5) Logic Integrity */}
            <CockpitTile
              label="Logic Integrity"
              value={`${ex.logic.openEndsPct}%`}
              secondary={`Predecessors: ${ex.logic.missingPredsCount} · Successors: ${ex.logic.missingSuccsCount}${
                ex.logic.relDensity !== null ? ` · Density: ${ex.logic.relDensity}` : ''
              }`}
              status={pctTileStatus(ex.logic.openEndsPct, 10, 25)}
              onClick={() => navigate('/app/analytics/quality?issue=missing_preds')}
            />

            {/* 6) Constraint Pressure */}
            <CockpitTile
              label="Constraint Pressure"
              value={`${ex.constraints.hardCount}`}
              secondary={`${ex.constraints.hardPct}% of incomplete activities`}
              status={pctTileStatus(ex.constraints.hardPct, 5, 15)}
              onClick={() => navigate('/app/analytics/quality?issue=constraints')}
            />

            {/* 7) Data Quality / Confidence */}
            <CockpitTile
              label="Data Confidence"
              value={`${ex.confidence.dateCoveragePct}%`}
              secondary={`Logic: ${ex.confidence.logicCoveragePct}% · ${ex.confidence.activityCount} activities · ${ex.confidence.calendarCount} calendars`}
              status={
                ex.confidence.dateCoveragePct >= 90 && ex.confidence.logicCoveragePct >= 80
                  ? 'ok'
                  : ex.confidence.dateCoveragePct >= 70
                    ? 'warn'
                    : 'critical'
              }
              onClick={() => navigate('/app/analytics/quality')}
            />
          </div>
        </div>

        {/* B) Health Rings */}
        <div>
          <SectionHeader title="Health Scores" className="mb-3" />
          <div className="flex items-start gap-4 p-4 rounded-lg border bg-card">
            <HealthRing
              label="Outcome"
              score={scores.outcome}
              reasons={scores.reasons.outcome.length > 0 ? scores.reasons.outcome : ['Starts at 100, penalised by negative float and critical %']}
              onClick={() => navigate('/app/analytics/health')}
            />
            <HealthRing
              label="Logic"
              score={scores.logic}
              reasons={scores.reasons.logic.length > 0 ? scores.reasons.logic : ['Starts at 100, penalised by open ends and low density']}
              onClick={() => navigate('/app/analytics/quality')}
            />
            <HealthRing
              label="Constraints"
              score={scores.constraints}
              reasons={scores.reasons.constraints.length > 0 ? scores.reasons.constraints : ['Starts at 100, penalised by hard constraint %']}
              onClick={() => navigate('/app/analytics/quality?issue=constraints')}
            />
            <HealthRing
              label="Deliverability"
              score={scores.deliverability}
              reasons={scores.reasons.deliverability.length > 0 ? scores.reasons.deliverability : ['Starts at 100, penalised by low coverage and outliers']}
              onClick={() => navigate('/app/analytics/health')}
            />
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION C: Milestone Governance Timeline
          ================================================================ */}
      <section>
        <SectionHeader
          title="Milestone Governance"
          subtitle={`Top ${milestones.length} milestones by forecast date`}
          className="mb-3"
        />
        <MilestoneStrip milestones={milestones} />
      </section>

      {/* ================================================================
          SECTION D: Drivers of Risk
          ================================================================ */}
      <section>
        <SectionHeader
          title="Drivers of Risk"
          subtitle="Ranked by composite score — worst first"
          className="mb-3"
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
            <h4 className="text-[11px] font-medium text-muted-foreground mb-2">
              Top Schedule Drivers (Activities)
            </h4>
            <ActivityDriverTable drivers={drivers.activities} />
          </div>
          <div>
            <h4 className="text-[11px] font-medium text-muted-foreground mb-2">
              Top WBS at Risk
            </h4>
            <WBSDriverTable drivers={drivers.wbs} />
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION E: Exceptions / Warning Lights
          ================================================================ */}
      <section>
        <SectionHeader
          title="Warning Lights"
          subtitle="Exceptions requiring attention"
          className="mb-3"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <ExceptionPanel
            title="Missing Predecessors"
            group={exceptions.openEnds.missingPreds}
            investigateHref={exceptions.openEnds.missingPreds.topExamples.length > 0
              ? toActivitiesUrl({ activity_ids: exceptions.openEnds.missingPreds.topExamples.map((ex) => ex.id) })
              : '/app/activities'}
          />
          <ExceptionPanel
            title="Missing Successors"
            group={exceptions.openEnds.missingSuccs}
            investigateHref={exceptions.openEnds.missingSuccs.topExamples.length > 0
              ? toActivitiesUrl({ activity_ids: exceptions.openEnds.missingSuccs.topExamples.map((ex) => ex.id) })
              : '/app/activities'}
          />
          <ExceptionPanel
            title="Hard Constraints"
            group={exceptions.constraints}
            investigateHref={exceptions.constraints.topExamples.length > 0
              ? toActivitiesUrl({ activity_ids: exceptions.constraints.topExamples.map((ex) => ex.id) })
              : '/app/activities'}
            extraField="type"
          />
          <ExceptionPanel
            title="Duration Outliers"
            group={exceptions.durations}
            investigateHref={exceptions.durations.topExamples.length > 0
              ? toActivitiesUrl({ activity_ids: exceptions.durations.topExamples.map((ex) => ex.id) })
              : '/app/activities'}
            extraField="days"
          />
          <ExceptionPanel
            title="Excessive Lags"
            group={exceptions.lags}
            investigateHref={exceptions.lags.topExamples.length > 0
              ? toActivitiesUrl({ activity_ids: exceptions.lags.topExamples.map((ex) => ex.id) })
              : '/app/activities'}
            extraField="lagDays"
          />
        </div>
      </section>

      {/* ================================================================
          SECTION F: What Changed
          ================================================================ */}
      <section>
        <SectionHeader title="Change Detection" className="mb-3" />
        <ChangeSummary data={change} />
      </section>
    </div>
  );
}
